package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable
import kotlin.math.roundToInt

/**
 * SH-106: whether clocking in and out at a workplace checks that the person is near it. The
 * phone reads its location once, at the tap (no background tracking); the clockShift function
 * (functions/geofence.js) applies the same [GeofenceRules] on the server, and firestore.rules let
 * the person write their own clock times directly only where the mode is [OFF].
 */
@Serializable
enum class GeofenceMode {
    /** No check (the default: workplaces saved before SH-106 keep working as they did). */
    OFF,
    /** Outside the radius, or without a location, the clock still works but the shift is flagged. */
    WARN,
    /** Outside the radius, or without a location, the clock is refused. */
    BLOCK;

    companion object {
        /** Tolerant lookup: unknown or missing means [OFF]. */
        fun fromName(name: String?): GeofenceMode = entries.firstOrNull { it.name == name } ?: OFF
    }
}

/** `workplaces/{id}.settings.geofence`. */
@Serializable
data class GeofenceSettings(
    val mode: GeofenceMode = GeofenceMode.OFF,
    val radiusMeters: Int = DEFAULT_RADIUS_METERS
) {
    companion object {
        const val DEFAULT_RADIUS_METERS = 150
        const val MIN_RADIUS_METERS = 50
        const val MAX_RADIUS_METERS = 1000
        /** The radii offered in the workplace settings. */
        val RADIUS_CHOICES = listOf(50, 100, 150, 200, 300, 500, 1000)

        fun radiusOrDefault(meters: Int?): Int =
            meters?.takeIf { it in MIN_RADIUS_METERS..MAX_RADIUS_METERS } ?: DEFAULT_RADIUS_METERS
    }
}

/** The result of the check stored with a clock time (never the coordinates themselves). */
enum class LocationCheck {
    INSIDE,
    OUTSIDE,
    /** No location: permission denied, no fix, or a device that has none (watch, desktop). */
    UNVERIFIED;

    companion object {
        fun fromName(name: String?): LocationCheck? = entries.firstOrNull { it.name == name }
    }
}

/** Why the device gave no location. */
enum class LocationUnavailable {
    /** The person did not allow location access. */
    DENIED,
    /** Allowed, but no position came in time (indoors, location services off). */
    NO_FIX,
    /** The device cannot tell its position (desktop, a watch asking through the phone). */
    UNSUPPORTED;

    companion object {
        fun fromName(name: String?): LocationUnavailable? = entries.firstOrNull { it.name == name }
    }
}

/** One reading of the device's position, taken at the tap. */
sealed interface LocationReading {
    data class Fix(val location: GeoLocation, val accuracyMeters: Double? = null) : LocationReading
    data class Unavailable(val reason: LocationUnavailable) : LocationReading
}

/** What the geofence says about one clock-in or clock-out. */
sealed interface GeofenceDecision {
    /** Clock; [check] null where there is no check (mode off, no workplace location). */
    data class Allow(val check: LocationCheck?, val distanceMeters: Int?) : GeofenceDecision

    /** Too far away (Block mode). */
    data class Outside(val distanceMeters: Int, val radiusMeters: Int) : GeofenceDecision

    /** No location to check (Block mode). */
    data class LocationRequired(val reason: LocationUnavailable) : GeofenceDecision
}

/**
 * The geofence rules, shared by the app (a quick answer before calling the server) and mirrored
 * by functions/geofence.js (the answer that counts). A workplace without a location has nothing
 * to measure from: the clock works without a check (the settings ask for the location before
 * the mode can be switched on, so this only happens when it was removed afterwards).
 */
object GeofenceRules {
    fun isActive(settings: GeofenceSettings, workplaceLocation: GeoLocation?): Boolean =
        settings.mode != GeofenceMode.OFF && workplaceLocation != null

    fun decide(settings: GeofenceSettings, workplaceLocation: GeoLocation?, reading: LocationReading): GeofenceDecision {
        if (workplaceLocation == null || settings.mode == GeofenceMode.OFF) return GeofenceDecision.Allow(null, null)
        val radius = GeofenceSettings.radiusOrDefault(settings.radiusMeters)
        return when (reading) {
            is LocationReading.Unavailable ->
                if (settings.mode == GeofenceMode.BLOCK) GeofenceDecision.LocationRequired(reading.reason)
                else GeofenceDecision.Allow(LocationCheck.UNVERIFIED, null)
            is LocationReading.Fix -> {
                val distance = reading.location.distanceMetersTo(workplaceLocation).roundToInt()
                when {
                    distance <= radius -> GeofenceDecision.Allow(LocationCheck.INSIDE, distance)
                    settings.mode == GeofenceMode.BLOCK -> GeofenceDecision.Outside(distance, radius)
                    else -> GeofenceDecision.Allow(LocationCheck.OUTSIDE, distance)
                }
            }
        }
    }
}

/** What a clock-in or clock-out recorded about the location (null check: none was made). */
data class ClockOutcome(
    val check: LocationCheck? = null,
    val distanceMeters: Int? = null,
    /** SH-134: a clock-in without a shift of one's own (covering a colleague, or a new shift). */
    val unplanned: UnplannedClockIn? = null,
    val shiftId: String? = null
)
